// Code generated by protoc-gen-ts_proto. DO NOT EDIT.
// versions:
//   protoc-gen-ts_proto  v2.11.7
//   protoc               v5.29.6
// source: text.proto

export const protobufPackage = 'compasspb';

/**
 * TextStatus represents the current state of a text message in its lifecycle.
 * Text messages progress through various states from creation to final delivery or failure.
 */
export enum TextStatus {
  /** TEXT_STATUS_UNSPECIFIED - Default/unknown status */
  TEXT_STATUS_UNSPECIFIED = 0,
  /** TEXT_STATUS_CREATED - Text message record created but not yet sent */
  TEXT_STATUS_CREATED = 1,
  /** TEXT_STATUS_IN_PROGRESS - Text message is being sent via SMS provider */
  TEXT_STATUS_IN_PROGRESS = 2,
  /** TEXT_STATUS_SUCCESS - Text message successfully delivered */
  TEXT_STATUS_SUCCESS = 3,
  /** TEXT_STATUS_FAILED - Text message delivery failed */
  TEXT_STATUS_FAILED = 4,
  /** TEXT_STATUS_IN_DRAFT - Text message is saved as draft, not sent */
  TEXT_STATUS_IN_DRAFT = 5,
}

/**
 * TextSignalStatus represents the status of a text message for workflow signals.
 * This is used when sending text delivery status updates from Twilio webhooks to workflows.
 */
export enum TextSignalStatus {
  TEXT_SIGNAL_STATUS_UNSPECIFIED = 0,
  /** TEXT_SIGNAL_STATUS_DELIVERED - Text was successfully delivered */
  TEXT_SIGNAL_STATUS_DELIVERED = 1,
  /** TEXT_SIGNAL_STATUS_UNDELIVERED - Text delivery failed */
  TEXT_SIGNAL_STATUS_UNDELIVERED = 2,
  /** TEXT_SIGNAL_STATUS_FAILED - Text processing failed */
  TEXT_SIGNAL_STATUS_FAILED = 3,
  /** TEXT_SIGNAL_STATUS_SENT - Delivery is in progress */
  TEXT_SIGNAL_STATUS_SENT = 4,
  /** TEXT_SIGNAL_STATUS_QUEUED - Message is queued for further progress */
  TEXT_SIGNAL_STATUS_QUEUED = 5,
}

/**
 * Text represents a text message entity sent via SMS.
 * Contains all information about a text message including sender, recipient,
 * content, delivery status, and timestamps.
 */
export interface Text {
  id?: string | undefined;
  case_id?: string | undefined;
  to?: string | undefined;
  from?: string | undefined;
  content?: string | undefined;
  status?: TextStatus | undefined;
  error?: string | undefined;
  created_at?: string | undefined;
  updated_at?: string | undefined;
}

/**
 * CreateTextRequest contains parameters for creating a new text message.
 * All fields are required except organization_id and program_id which are
 * extracted from the URL path.
 */
export interface CreateTextRequest {
  organization_id?: string | undefined;
  program_id?: string | undefined;
  case_record_id?: string | undefined;
  to?: string | undefined;
  from?: string | undefined;
  content?: string | undefined;
}

/**
 * CreateTextResponse returns the newly created text message.
 * Contains the complete text message entity with generated ID and timestamps.
 */
export interface CreateTextResponse {
  text?: Text | undefined;
}

/**
 * UpdateTextRequest contains parameters for updating an existing text message.
 * All fields except text_id, organization_id, program_id, and case_record_id are optional.
 * Only provided fields will be updated.
 */
export interface UpdateTextRequest {
  organization_id?: string | undefined;
  program_id?: string | undefined;
  case_record_id?: string | undefined;
  text_id?: string | undefined;
  to?: string | undefined;
  from?: string | undefined;
  content?: string | undefined;
  status?: TextStatus | undefined;
  error?: string | undefined;
}

/**
 * UpdateTextResponse returns the updated text message.
 * Contains the complete text message entity with all current values.
 */
export interface UpdateTextResponse {
  text?: Text | undefined;
}

/**
 * GetTextRequest contains parameters for retrieving a single text message.
 * Requires the organization, program, case record, and text message identifiers.
 */
export interface GetTextRequest {
  organization_id?: string | undefined;
  program_id?: string | undefined;
  case_record_id?: string | undefined;
  text_id?: string | undefined;
}

/**
 * GetTextResponse returns the requested text message.
 * Contains the complete text message entity with all fields.
 */
export interface GetTextResponse {
  text?: Text | undefined;
}

/**
 * GetTextsRequest contains parameters for retrieving all text messages for a case.
 * Supports pagination through the limit parameter.
 */
export interface GetTextsRequest {
  organization_id?: string | undefined;
  program_id?: string | undefined;
  case_record_id?: string | undefined;
  limit?: number | undefined;
}

/**
 * GetTextsResponse returns a list of text messages for the case record.
 * Text messages are ordered by creation time (newest first).
 */
export interface GetTextsResponse {
  texts?: Text[] | undefined;
}

/**
 * TextService provides operations for SMS text messaging.
 * This service handles sending and receiving text messages as part of
 * case record workflows and patient/member communication. It supports
 * creating, updating, retrieving, and listing text messages associated
 * with case records.
 */
export interface TextService {
  /**
   * CreateText creates a new text message for a case record.
   * The text is sent via SMS to the specified recipient.
   */
  createText(request: CreateTextRequest): Promise<CreateTextResponse>;
  /**
   * UpdateText updates an existing text message.
   * This allows modifying text content, status, and error information.
   */
  updateText(request: UpdateTextRequest): Promise<UpdateTextResponse>;
  /**
   * GetText retrieves a single text message by its ID.
   * Returns the complete text message details including status and timestamps.
   */
  getText(request: GetTextRequest): Promise<GetTextResponse>;
  /**
   * GetTexts retrieves all text messages for a case record.
   * Returns a list of text messages ordered by creation time, with optional limit.
   */
  getTexts(request: GetTextsRequest): Promise<GetTextsResponse>;
}
